import React, { useContext, useState } from 'react';
import { PeopleAlt, Person, Visibility, VisibilityOff, Lock } from '@mui/icons-material';
import {
  GetUsersDocument,
  IGetUsersQuery,
  IRole,
  useCreateUserMutation,
  useGetRolesQuery
} from '../../../generated/graphql';
import { AppContext } from '../../../AppContext';
import { Actions } from '../../../enums/ActionEnums';
import { sameUsernameExists } from '../../../utils/clientUtils';
import { getLabelColor, getRequiredFieldLabelColor } from '../../../utils/styleUtils';
import { Roles } from '../../../enums/RoleEnums';
import { Box, IconButton, Chip } from '@mui/material';
import DialogBox from '../../Common/Layout/DialogBox';
import InputComponent from '../../Common/Fields/InputComponent';
import CancelButton from '../../Common/Buttons/CancelButton';
import SaveButton from '../../Common/Buttons/SaveButton';
import AutocompleteComponent from '../../Common/Fields/AutocompleteComponent';
import { coloring } from './UsersList';
import { Colors } from '../../../utils/theme';

type IProps = {
  handleClose: () => void;
};

export const ErrorTexts = {
  username: 'Username is required',
  usernameExist: 'Username already exist',
  firstName: 'First name is required',
  lastName: 'Last name is required',
  password: 'Password is required',
  confirmPassword: 'Confirm password is required',
  passwordMismatch: 'Passwords do not match'
};

type IErrors = {
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  userGroup: string | null;
  password: string | null;
  confirmPassword: string | null;
  addUserErrors?: (string | null)[] | null | undefined;
};

const defaultErrorObj: IErrors = {
  username: null,
  firstName: null,
  lastName: null,
  userGroup: null,
  password: null,
  confirmPassword: null
};

const CreateUser: React.FunctionComponent<IProps> = ({ handleClose }: IProps) => {
  const { data } = useGetRolesQuery();
  const { dispatch } = useContext(AppContext);
  const [username, setUsername] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [userGroup, setUserGroup] = useState<IRole[]>([]);
  const [formErrors, setFormErrors] = useState(defaultErrorObj);
  const [addUser, addUserResult] = useCreateUserMutation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleUsernameInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    const trimedValue = value.trim();
    if (trimedValue && trimedValue.length > 1) {
      setFormErrors({ ...formErrors, username: null });
    } else {
      setFormErrors({ ...formErrors, username: ErrorTexts.username });
    }
    setUsername(value);
  };

  const handleFirstNameInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    const trimedValue = value.trim();
    if (trimedValue && trimedValue.length > 1) {
      setFormErrors({ ...formErrors, firstName: null });
    } else {
      setFormErrors({ ...formErrors, firstName: ErrorTexts.firstName });
    }
    const finalValue = value.charAt(0).toUpperCase() + value.slice(1);
    setFirstName(finalValue);
  };

  const handleLastNameInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    const trimedValue = value.trim();
    if (trimedValue && trimedValue.length > 1) {
      setFormErrors({ ...formErrors, lastName: null });
    } else {
      setFormErrors({ ...formErrors, lastName: ErrorTexts.lastName });
    }
    const finalValue = value.charAt(0).toUpperCase() + value.slice(1);
    setLastName(finalValue);
  };

  const handlePasswordInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    const trimedValue = value.trim();
    if (trimedValue && trimedValue.length > 1) {
      setFormErrors({ ...formErrors, password: null });
    } else {
      setFormErrors({ ...formErrors, password: ErrorTexts.password });
    }
    setPassword(value);
  };

  const handleConfirmPasswordInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;
    const trimedValue = value.trim();
    if (trimedValue && trimedValue.length > 1) {
      setFormErrors({ ...formErrors, confirmPassword: null });
    } else {
      setFormErrors({ ...formErrors, confirmPassword: ErrorTexts.confirmPassword });
    }
    setConfirmPassword(value);
  };

  const passEqualityChecker = (firstPassEntry: string, secondPassEntry: string) => {
    return firstPassEntry === secondPassEntry;
  };

  const submitForm = () => {
    const errors = {
      username: null,
      firstName: null,
      lastName: null,
      password: null,
      confirmPassword: null
    } as IErrors;
    let areErrors = false;
    if (!username || username.length < 2) {
      areErrors = true;
      errors.username = ErrorTexts.username;
    }
    if (!firstName || firstName.length < 2) {
      areErrors = true;
      errors.firstName = ErrorTexts.firstName;
    }
    if (!lastName || lastName.length < 2) {
      areErrors = true;
      errors.lastName = ErrorTexts.lastName;
    }
    if (!password || password.length < 2) {
      areErrors = true;
      errors.password = ErrorTexts.password;
    }
    if (!confirmPassword || confirmPassword.length < 2) {
      areErrors = true;
      errors.confirmPassword = ErrorTexts.confirmPassword;
    }
    if (!areErrors && sameUsernameExists(username.trim())) {
      areErrors = true;
      errors.username = ErrorTexts.usernameExist;
    }
    if (!areErrors && password !== confirmPassword) {
      areErrors = true;
      errors.confirmPassword = ErrorTexts.passwordMismatch;
      errors.password = ErrorTexts.passwordMismatch;
    }
    if (areErrors) {
      setFormErrors(errors);
    } else {
      handleCreate();
    }
  };

  const handleCreate = () => {
    if (!passEqualityChecker(password, confirmPassword)) {
      setFormErrors({
        ...formErrors,
        password: 'Passwords do not match',
        confirmPassword: 'Passwords do not match'
      });
    }
    dispatch({ type: Actions.HideConfirmation });
    addUser({
      variables: {
        input: {
          username: username.trim(),
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          password: password.trim(),
          confirm_password: confirmPassword.trim(),
          user_role_ids: converter(userGroup)
        }
      },
      update(cache, { data }) {
        if (data?.result) {
          const userQuery = cache.readQuery({
            query: GetUsersDocument
          }) as IGetUsersQuery;
          if (userQuery.result) {
            const cacheUser = userQuery.result.find((u) => u?.id === data.result.id);
            if (!cacheUser) {
              const newData = [data.result, ...userQuery.result];
              cache.writeQuery({
                query: GetUsersDocument,
                data: { result: newData }
              });
            }
          }
          dispatch({
            type: Actions.ShowMessage,
            payload: {
              text: `User ${data.result.username} created`,
              severity: 'success',
              autoHide: 2000
            }
          });
          handleClose();
        }
      }
    });
  };

  const converter = (arrOfObj: IRole[]) => {
    const res = [];
    for (let i = 0; i < arrOfObj.length; i++) {
      res.push(parseInt(arrOfObj[i].id));
    }
    return res;
  };

  const handleUserGroupsChange = (_: unknown, newValue: IRole[]) => {
    setUserGroup(newValue);
  };

  //   const handleRoleDelete = (role: IRole) => {
  //     setUserGroup(userGroup.filter((g) => g.id !== role.id));
  //   };

  const getOptionLabel = (option: IRole): string => {
    if (option && option.name) {
      return option.name.replace('_', ' ') as string;
    } else {
      return '';
    }
  };
  return (
    <DialogBox id="new-user-dialog" width={1400} title={'Create User'} onClose={handleClose}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: '20px 44px',
          padding: '45px 395px',
          height: '626px'
        }}
      >
        <InputComponent
          id="create-user-username"
          label="Username"
          value={username}
          onChange={handleUsernameInput}
          errorMsg={formErrors.username}
          disabled={addUserResult.loading}
          additionalStyle={{ gridColumn: 'span 2', marginTop: '50px' }}
          startAdornment={
            <Person
              style={{
                marginRight: '12px',
                color: getRequiredFieldLabelColor(!addUserResult.loading, formErrors.username)
              }}
            />
          }
        />
        <InputComponent
          id="create-user-firstName"
          label="First Name"
          value={firstName}
          onChange={handleFirstNameInput}
          errorMsg={formErrors.firstName}
          disabled={addUserResult.loading}
          additionalStyle={{ gridColumn: 'span 1' }}
          startAdornment={
            <Person
              style={{
                marginRight: '12px',
                color: getRequiredFieldLabelColor(!addUserResult.loading, formErrors.firstName)
              }}
            />
          }
        />
        <InputComponent
          id="create-user-last-name"
          label="Last Name"
          value={lastName}
          onChange={handleLastNameInput}
          errorMsg={formErrors.lastName}
          disabled={addUserResult.loading}
          additionalStyle={{ gridColumn: 'span 1' }}
          startAdornment={
            <Person
              style={{
                marginRight: '12px',
                color: getRequiredFieldLabelColor(!addUserResult.loading, formErrors.lastName)
              }}
            />
          }
        />
        <InputComponent
          id="create-user-password"
          label="Password"
          value={password}
          type={showPassword ? 'text' : 'password'}
          onChange={handlePasswordInput}
          errorMsg={formErrors.password}
          disabled={addUserResult.loading}
          additionalStyle={{ gridColumn: 'span 2' }}
          startAdornment={
            <Lock
              style={{
                marginRight: '12px',
                color: getRequiredFieldLabelColor(!addUserResult.loading, formErrors.password)
              }}
            />
          }
          endAdornment={
            <IconButton
              aria-label="toggle password visibility"
              onClick={() => setShowPassword(!showPassword)}
              edge="end"
            >
              {showPassword ? <Visibility /> : <VisibilityOff />}
            </IconButton>
          }
        />
        <InputComponent
          id="create-user-confirm-password"
          label="Confirm Password"
          value={confirmPassword}
          onChange={handleConfirmPasswordInput}
          errorMsg={formErrors.confirmPassword}
          type={showConfirmPassword ? 'text' : 'password'}
          disabled={addUserResult.loading}
          additionalStyle={{ gridColumn: 'span 2' }}
          startAdornment={
            <Lock
              sx={{
                marginRight: '12px',
                color: getRequiredFieldLabelColor(
                  !addUserResult.loading,
                  formErrors.confirmPassword
                )
              }}
            />
          }
          endAdornment={
            <IconButton
              aria-label="toggle confirm password visibility"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              edge="end"
            >
              {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
            </IconButton>
          }
        />
        <AutocompleteComponent
          id="create-user-assign-user-group-autocomplete"
          label="User Groups"
          values={userGroup}
          options={
            (data?.result.filter(
              (r) => r?.name !== Roles.Admin && r?.name !== Roles.InvoiceEditor
            ) as never[]) || []
          }
          isSingle={false}
          onChange={handleUserGroupsChange}
          disabled={addUserResult.loading}
          getOptionLabel={(option: IRole) => getOptionLabel(option)}
          additionalStyle={{ gridColumn: 'span 2' }}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                sx={{
                  backgroundColor: Colors.white,
                  color: coloring(option.name),
                  '& .MuiChip-deleteIcon': {
                    width: '20px',
                    height: '20px',
                    color: Colors.skyBlue,
                    '&:hover': {
                      color: Colors.main
                    }
                  }
                }}
                variant="outlined"
                label={`${option.name.replace('_', ' ')}`}
                {...getTagProps({ index })}
              />
            ))
          }
          startAdornment={
            <PeopleAlt
              style={{
                marginRight: '12px',
                color: getLabelColor(!addUserResult.loading)
              }}
            />
          }
        />
        <Box className="centralize" sx={{ gridColumn: 'span 2', marginTop: '50px' }}>
          <CancelButton
            id="cancel-create-user-button"
            disabled={addUserResult.loading}
            onClick={handleClose}
            text={'Cancel'}
          />
          <SaveButton
            id="save-create-user-button"
            disabled={addUserResult.loading}
            onClick={submitForm}
            text={'Create'}
          />
        </Box>
      </Box>
    </DialogBox>
  );
};

export default CreateUser;
